import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

const API_URL = import.meta.env.VITE_API_URL || "https://eleven11-i6v4.onrender.com";

export default function ShippingAddressScreen() {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();

  const saved = user?.shippingAddress || {};

  const [form, setForm] = useState({
    address:   saved.address   || "",
    apartment: saved.apartment || "",
    city:      saved.city      || "",
    state:     saved.state     || "",
    zip:       saved.zip       || "",
    country:   saved.country   || "United States",
  });

  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError]     = useState("");

  const update = (field, value) =>
    setForm(prev => ({ ...prev, [field]: value }));

  const handleSave = async () => {
    setSaving(true);
    setError("");
    setSuccess(false);

    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_URL}/auth/me/address`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(form),
      });

      if (!res.ok) throw new Error("Failed to save");

      await refreshUser();
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch {
      setError("Could not save address. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.container}>

        <div style={styles.header}>
          <button style={styles.back} onClick={() => navigate("/account")}>
            ‹ Back
          </button>
          <div>
            <p style={styles.kicker}>ACCOUNT SETTINGS</p>
            <h1 style={styles.title}>Shipping Address</h1>
          </div>
        </div>

        <div style={styles.card}>
          <p style={styles.sectionLabel}>DELIVERY ADDRESS</p>

          <div style={styles.inputGroup}>
            <label style={styles.label}>Street Address</label>
            <input style={styles.input} placeholder="1234 Ocean Blvd"
              value={form.address} onChange={e => update("address", e.target.value)} />
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.label}>Apartment / Suite</label>
            <input style={styles.input} placeholder="Optional"
              value={form.apartment} onChange={e => update("apartment", e.target.value)} />
          </div>

          <div style={styles.triGrid}>
            <div style={styles.inputGroup}>
              <label style={styles.label}>City</label>
              <input style={styles.input} placeholder="Miami" value={form.city}
                onChange={e => update("city", e.target.value.replace(/\b\w/g, c => c.toUpperCase()))} />
            </div>
            <div style={styles.inputGroup}>
              <label style={styles.label}>State</label>
              <input style={styles.input} placeholder="FL" value={form.state}
                onChange={e => update("state", e.target.value.toUpperCase().slice(0, 2))} />
            </div>
            <div style={styles.inputGroup}>
              <label style={styles.label}>ZIP Code</label>
              <input style={styles.input} placeholder="33101" value={form.zip}
                onChange={e => update("zip", e.target.value.replace(/\D/g, "").slice(0, 5))} />
            </div>
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.label}>Country</label>
            <input style={{ ...styles.input, color: "#888", background: "#f9f9fb" }}
              value={form.country} readOnly />
          </div>

          {error   && <p style={styles.errorMsg}>{error}</p>}
          {success && <div style={styles.successBanner}>✓ Address saved successfully</div>}

          <button style={{ ...styles.saveBtn, opacity: saving ? 0.7 : 1 }}
            onClick={handleSave} disabled={saving}>
            {saving ? "Saving..." : "Save Address"}
          </button>
        </div>

        {saved.address && (
          <div style={styles.previewCard}>
            <p style={styles.previewLabel}>CURRENTLY SAVED</p>
            <p style={styles.previewLine}>{saved.address}{saved.apartment ? `, ${saved.apartment}` : ""}</p>
            <p style={styles.previewLine}>{saved.city}, {saved.state} {saved.zip}</p>
            <p style={styles.previewLine}>{saved.country}</p>
          </div>
        )}

      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: "100vh",
    background: "linear-gradient(180deg, #f5f7fb 0%, #eef1f7 45%, #f7f8fb 100%)",
    paddingTop: 90, paddingBottom: 80,
    fontFamily: "-apple-system, BlinkMacSystemFont, Inter, Helvetica, Arial, sans-serif",
  },
  container: { width: "min(680px, calc(100% - 40px))", margin: "0 auto" },
  header: { marginBottom: 28 },
  back: { background: "none", border: "none", fontSize: 17, fontWeight: 700, color: "#0a84ff", cursor: "pointer", padding: 0, marginBottom: 16, display: "block" },
  kicker: { margin: 0, fontSize: 12, fontWeight: 800, letterSpacing: 2.4, color: "#7b7f89" },
  title: { margin: "6px 0 0", fontSize: 42, letterSpacing: -1.5, fontWeight: 800, color: "#050505" },
  card: { borderRadius: 28, background: "rgba(255,255,255,0.85)", backdropFilter: "blur(24px)", border: "1px solid rgba(255,255,255,0.8)", boxShadow: "0 26px 60px rgba(24,35,60,0.1)", padding: "32px 36px", marginBottom: 20 },
  sectionLabel: { margin: "0 0 24px", fontSize: 12, fontWeight: 900, color: "#70747e", letterSpacing: 2.2 },
  inputGroup: { marginBottom: 20 },
  label: { display: "block", fontSize: 13, fontWeight: 700, color: "#6b7280", marginBottom: 8 },
  input: { width: "100%", padding: "14px 18px", borderRadius: 14, border: "1px solid #E0E3EA", background: "#fff", fontSize: 16, fontWeight: 500, color: "#111", outline: "none", boxSizing: "border-box", fontFamily: "inherit" },
  triGrid: { display: "grid", gridTemplateColumns: "1.5fr 0.7fr 1fr", gap: 14 },
  errorMsg: { color: "#d93025", fontSize: 14, fontWeight: 600, margin: "0 0 16px" },
  successBanner: { background: "#ecfdf5", border: "1px solid #6ee7b7", borderRadius: 12, padding: "12px 18px", fontSize: 14, fontWeight: 700, color: "#065f46", marginBottom: 16 },
  saveBtn: { width: "100%", padding: "17px", borderRadius: 999, border: "none", background: "#111", color: "#fff", fontSize: 16, fontWeight: 800, cursor: "pointer", marginTop: 8 },
  previewCard: { borderRadius: 20, background: "rgba(255,255,255,0.65)", border: "1px solid rgba(255,255,255,0.75)", padding: "22px 28px", boxShadow: "0 8px 30px rgba(24,35,60,0.06)" },
  previewLabel: { margin: "0 0 12px", fontSize: 11, fontWeight: 900, color: "#9ca3af", letterSpacing: 2 },
  previewLine: { margin: "0 0 4px", fontSize: 15, color: "#374151", fontWeight: 500 },
};